import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { ToastrService } from 'ngx-toastr';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import { DatePipe } from '@angular/common';
import { ColDef, GridApi, GridReadyEvent, PostSortRowsParams, ColumnState, GetRowIdParams } from 'ag-grid-community';
import { ExportDataService } from '../../../shared/services/export-data.service';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DrillDownColumnType = 'group' | 'count' | 'sum';
export type DrillDownColumnConfig = { type: DrillDownColumnType; columns: string };

interface DrillDownNode {
  id: string;
  name: string;
  level: number;
  groupLabel: string;
  rowCount: number;
  counts: Record<string, number>;
  sums: Record<string, number>;
  sumHasDollar: Record<string, boolean>;
  distinctTracker: Record<string, Set<string>>;
  children: DrillDownNode[];
  /** Application rows collected when this is the deepest group level */
  appRows: any[];
  expanded: boolean;
  /** Whether the app-list sub-panel is shown */
  appsExpanded: boolean;
  isLeaf: boolean;
}

interface DrillDownGridRow {
  node: DrillDownNode;
  group: string;
  /** marks the synthetic "app row" entries rendered under a leaf node */
  isAppRow?: boolean;
  appEntry?: DrillDownAppEntry;
}

interface DrillDownAppEntry {
  id: string;
  name: string;
  counts: Record<string, number>;
  sums: Record<string, number>;
  sumHasDollar: Record<string, boolean>;
  distinctTracker: Record<string, Set<string>>;
}

interface DrillDownMetricRule {
  key: string;
  aggregate: 'sum' | 'count';
  distinct: boolean;
  sourceField?: string;
  distinctKey?: string;
  conditionField?: string;
  conditionValues: string[];
  sourceFields?: string[];
  operation?: string;
  /** Display order from columnInfo — used to sort columns when provided */
  order?: number;
  /** Optional formula to compute a percentage shown in brackets after the cell value.
   *  Column names in the formula are replaced with their aggregated values at render time.
   *  Example: "(Pending Charges * 100)/(Pending Charges + Completed Charges)" */
  showPercentage?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-inbuilt-drill-down-matrix',
  standalone: false,
  templateUrl: './inbuilt-drill-down-matrix.component.html',
  styleUrl: './inbuilt-drill-down-matrix.component.css'
})
export class InbuiltDrillDownMatrixComponent implements OnInit, OnDestroy {

  @Input() columns: DrillDownColumnConfig[] = [];
  @Input() config: any;
  @Input() elementData: any = null;
  @ViewChild('matrixContainer', { static: false }) private matrixContainer?: ElementRef<HTMLElement>;

  // ── UI state ──────────────────────────────────────────────────────────────
  loading = false;
  matrixLoader = false;
  showMatrix = false;
  allExpanded = false;
  isExporting = false;
  isDownloadDisabled = false;
  tableDataTotalCount = 0;

  // ── Data & time ───────────────────────────────────────────────────────────
  tableData: any[] = [];
  rawTableDataForDownload: any[] = [];
  currentDate: Date = new Date();
  currentTime = '';
  matrixGeneratedRowCount = 0;

  // ── AG Grid ───────────────────────────────────────────────────────────────
  matrixRowData: DrillDownGridRow[] = [];
  matrixColumnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true
  };

  // ── Parsed column lists ───────────────────────────────────────────────────
  groupColumns: string[] = [];
  countColumns: string[] = [];
  sumColumns: string[] = [];
  attributeColumns: string[] = [];
  /** Columns that identify the application name in app-list rows */
  appNameColumns: string[] = [];
  /** Maximum depth level: group levels 0..maxLevel-1, level maxLevel shows app list */
  maxGroupLevel = 0;

  // ── Internals ─────────────────────────────────────────────────────────────
  private matrixRoots: DrillDownNode[] = [];
  private nodeMap = new Map<string, DrillDownNode>();
  private resolvedGroupKeys: Array<string | undefined> = [];
  private resolvedCountKeys: Array<string | undefined> = [];
  private resolvedSumKeys: Array<string | undefined> = [];
  private selectedFilters: any = {};
  private gridApi: GridApi | null = null;
  /** True once autoSizeAllColumns has been called after the first data load for the current dataset. */
  private metricColumnsAutoSized = false;
  private resizeDebounceHandle: any = null;

  private readonly refreshCancel$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private readonly norm = (v: string) => (v ?? '').toLowerCase().replace(/[\s_\-]+/g, '');
  private groupByColumns: string[] = [];
  private metricRules: DrillDownMetricRule[] = [];

  readonly postSortRows = (params: PostSortRowsParams<DrillDownGridRow>): void => {
    const ordered = this.buildSortedVisibleRowNodes(params.nodes || []);
    if (ordered.length !== params.nodes.length) return;
    params.nodes.length = 0;
    ordered.forEach((rowNode: any) => params.nodes.push(rowNode));
  };

  /** Stable row identity so AG Grid can diff/patch rows instead of tearing down the whole row model on every refresh. */
  readonly getRowId = (params: GetRowIdParams<DrillDownGridRow>): string => this.getRowSortKey(params.data) ?? String(Math.random());

  constructor(
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private inbuiltUtilityService: InbuiltUtilityService,
    private datePipe: DatePipe,
    private exportDataService: ExportDataService
  ) {}

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.updateGmtTimestamp();
    this.hydrateColumnConfig();
    this.selectedFilters = this.config?.selectedFilters || {};
    this.subscribeToFilterEvents();
    this.showMatrix = true;
    this.resetAndFetch();
  }

  ngOnDestroy(): void {
    this.refreshCancel$.next();
    this.refreshCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeDebounceHandle) clearTimeout(this.resizeDebounceHandle);
    this.gridApi = null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.resizeDebounceHandle) clearTimeout(this.resizeDebounceHandle);
    this.resizeDebounceHandle = setTimeout(() => {
      if (!this.canUseGridApi()) return;
      // Reset to natural content widths first — columns previously stretched to fill a
      // large screen would otherwise look "overflowing" once the viewport shrinks (e.g.
      // browser zoom-in), causing the fit check below to wrongly skip re-fitting.
      this.gridApi!.autoSizeAllColumns();
      this.fitColumnsToAvailableWidth();
    }, 150);
  }

  // ─── Filter event subscription ──────────────────────────────────────────────

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) return;
        this.showMatrix = true;
        this.selectedFilters = event?.selectedFilters || {};
        this.resetAndFetch();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.elementData || !this.config) return false;

    const evReport = String(event?.reportNameFromQueryParams ?? '');
    const evPage   = String(event?.currentPage?.key ?? event?.page ?? '');
    const evTab    = String(event?.currentTab?.key  ?? event?.tab  ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage   = String(this.config?.currentPage?.key ?? '');
    const myTab    = String(this.config?.currentTab?.key  ?? '');

    if (evReport !== myReport || evPage !== myPage || evTab !== myTab) return false;
    if (typeof event?.showTable === 'boolean') return true;

    const filterName = String(event?.filterName ?? '');
    if (!filterName) return false;

    const rawDeps = this.elementData?.filterList;
    if (!rawDeps) return true;

    let deps: any[] = [];
    if (Array.isArray(rawDeps)) {
      deps = rawDeps;
    } else if (typeof rawDeps === 'string') {
      try { deps = JSON.parse(rawDeps); } catch { deps = [rawDeps]; }
    }
    if (deps.length === 0) return true;

    return deps.some((dep: any) => {
      const k = dep && typeof dep === 'object' ? String(Object.keys(dep)[0]   ?? '') : String(dep ?? '');
      const v = dep && typeof dep === 'object' ? String(Object.values(dep)[0] ?? '') : String(dep ?? '');
      return k === filterName || v === filterName || String(dep ?? '') === filterName;
    });
  }

  // ─── Data fetching ──────────────────────────────────────────────────────────

  private resetAndFetch(): void {
    this.refreshCancel$.next();
    this.tableData = [];
    this.rawTableDataForDownload = [];
    this.tableDataTotalCount = 0;
    this.matrixGeneratedRowCount = 0;
    this.attributeColumns = [];
    this.metricColumnsAutoSized = false;
    this.clearMatrixData();
    this.matrixLoader = true;
    this.getMatrixData();
  }

  private getMatrixData(): void {
    this.loading = true;
    this.updateGmtTimestamp();

    const payload = {
      report:      this.config?.reportNameFromQueryParams,
      page:        this.config?.currentPage?.key || '',
      tab:         this.config?.currentTab?.key  || '',
      elementName: this.elementData?.name  || '',
      elementType: this.elementData?.type  || '',
      download:    false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters)
    };

    this.reportsService.getInbuildTableData(payload)
      .pipe(takeUntil(this.refreshCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const newRows: any[] = res?.data || [];
          this.tableDataTotalCount = res?.totalCount || 0;
          this.rawTableDataForDownload = [...newRows];

          const resolvedColumnInfo = res?.columnInfo
            ?? this.elementData?.config?.columnInfo
            ?? this.config?.columnInfo;
          this.applyResponseConfig(res?.groupByColumns, resolvedColumnInfo, newRows);

          if (newRows.length === 0) {
            this.tableData = [];
            this.clearMatrixData();
          } else {
            this.tableData = [...newRows];
            this.attributeColumns = Object.keys(newRows[0] || {});
            this.buildGridColumns();
            this.appendRowsToMatrix(this.tableData);
          }

          this.loading      = false;
          this.matrixLoader = false;
        },
        error: (err: any) => {
          this.loading      = false;
          this.matrixLoader = false;
          this.handleErrors(err);
        }
      });
  }

  private applyResponseConfig(groupByColumns: any, columnInfo: any, rows: any[]): void {
    const sampleRow = Array.isArray(rows) && rows.length > 0 ? rows[0] : {};
    const allKeys = Object.keys(sampleRow || {});

    const responseGroupBy = Array.isArray(groupByColumns)
      ? groupByColumns.map((item: any) => String(item ?? '').trim()).filter(Boolean)
      : [];

    const configuredGroupBy = this.resolveGroupByColumns();
    const preferredGroupBy = responseGroupBy.length > 0 ? responseGroupBy : configuredGroupBy;

    this.groupByColumns = preferredGroupBy;
    this.groupColumns = [...preferredGroupBy];
    this.maxGroupLevel = this.groupColumns.length;

    const groupSet = new Set(this.groupColumns.map((k: string) => this.norm(k)));

    this.metricRules = this.buildMetricRules(columnInfo, allKeys, this.groupColumns);
    this.countColumns = this.metricRules.filter(r => r.aggregate === 'count').map(r => r.key);
    this.sumColumns   = this.metricRules.filter(r => r.aggregate === 'sum').map(r => r.key);

    if (this.metricRules.length === 0 && allKeys.length > 0) {
      const fallbackMetricColumns = allKeys.filter((k: string) => !groupSet.has(this.norm(k)));
      this.metricRules = fallbackMetricColumns.map((k: string) => ({
        key: k, aggregate: 'sum', distinct: false, conditionValues: []
      }));
      this.sumColumns   = [...fallbackMetricColumns];
      this.countColumns = [];
    }

    this.countColumns = [...new Set(this.countColumns)];
    this.sumColumns   = [...new Set(this.sumColumns)];
    this.appNameColumns = this.resolveAppNameColumns(allKeys);
  }

  // ─── Matrix tree ────────────────────────────────────────────────────────────

  private buildMetricRules(columnInfo: any, allKeys: string[], groupColumns: string[]): DrillDownMetricRule[] {
    if (!columnInfo || typeof columnInfo !== 'object' || Array.isArray(columnInfo)) return [];

    const groupSet = new Set(groupColumns.map((k: string) => this.norm(k)));
    const rules: DrillDownMetricRule[] = [];

    for (const [rawKey, rawDef] of Object.entries(columnInfo)) {
      const resolvedKey = allKeys.find((k: string) => this.norm(k) === this.norm(String(rawKey))) || String(rawKey);
      if (!resolvedKey || groupSet.has(this.norm(resolvedKey))) continue;
      const parsedRule = this.parseMetricDefinition(rawDef, resolvedKey, allKeys);
      if (!parsedRule) continue;

      if (rawDef && typeof rawDef === 'object' && !Array.isArray(rawDef)) {
        const rawOrder = (rawDef as any).order;
        if (rawOrder !== undefined && rawOrder !== null && !isNaN(Number(rawOrder))) {
          parsedRule.order = Number(rawOrder);
        }
        const rawPct = (rawDef as any).showPercentage;
        if (rawPct && typeof rawPct === 'string' && rawPct.trim()) {
          parsedRule.showPercentage = rawPct.trim();
        }
      }

      rules.push(parsedRule);
    }

    // Sort by order when at least one rule has it; rules without order keep their relative position at the end
    const hasAnyOrder = rules.some((r: DrillDownMetricRule) => r.order !== undefined);
    if (hasAnyOrder) {
      rules.sort((a: DrillDownMetricRule, b: DrillDownMetricRule) => {
        const aHasOrder = a.order !== undefined;
        const bHasOrder = b.order !== undefined;
        if (aHasOrder && bHasOrder) return (a.order as number) - (b.order as number);
        if (aHasOrder) return -1;
        if (bHasOrder) return 1;
        return 0;
      });
    }

    return rules;
  }

  private parseMetricDefinition(definition: any, resolvedKey: string, allKeys: string[]): DrillDownMetricRule | null {
    if (typeof definition === 'string') {
      const aggregate = this.normalizeAggregate(definition);
      return aggregate ? { key: resolvedKey, aggregate, distinct: false, conditionValues: [] } : null;
    }

    if (!definition || typeof definition !== 'object' || Array.isArray(definition)) return null;

    const aggregate = this.normalizeAggregate(
      definition.aggregate ?? definition.aggregation ?? definition.type ?? definition.metric ?? definition.value
    );
    if (!aggregate) return null;

    const distinct = !!definition.distinct;
    const sourceField = definition.sourceField ?? definition.sourceKey ?? definition.source
      ? this.resolveColumnName(allKeys, String(definition.sourceField ?? definition.sourceKey ?? definition.source))
      : undefined;
    const distinctKey = definition.distinctKey ?? definition.distinctOn ?? definition.key
      ? this.resolveColumnName(allKeys, String(definition.distinctKey ?? definition.distinctOn ?? definition.key))
      : (sourceField || resolvedKey);

    const operation = String(definition.operation ?? '').trim().toLowerCase();
    const mathExpression = String(definition.condition ?? '').trim();
    const hasMathematicalCondition = aggregate === 'sum' && this.isMathematicalCondition(mathExpression, allKeys);

    if (operation === 'mathematical' || hasMathematicalCondition) {
      const sourceFields = this.parseMathematicalSourceFields(mathExpression, allKeys);
      const mathDistinctKeyCandidate = definition?.distinctKey ?? definition?.distinctOn;
      const mathDistinctKey = mathDistinctKeyCandidate
        ? this.resolveColumnName(allKeys, String(mathDistinctKeyCandidate))
        : undefined;
      return {
        key: resolvedKey,
        aggregate,
        distinct: false,
        operation: 'mathematical',
        sourceFields,
        conditionValues: [],
        ...(mathDistinctKey ? { distinctKey: mathDistinctKey } : {}),
      };
    }

    const parsedCondition = this.parseMetricCondition(definition.condition, allKeys, resolvedKey);

    return { key: resolvedKey, aggregate, distinct, sourceField, distinctKey, conditionField: parsedCondition.field, conditionValues: parsedCondition.values };
  }

  private normalizeAggregate(value: any): 'sum' | 'count' | null {
    const s = String(value ?? '').trim().toLowerCase();
    if (s === 'sum') return 'sum';
    if (s === 'count') return 'count';
    return null;
  }

  private parseMetricCondition(rawCondition: any, allKeys: string[], fallbackField: string): { field?: string; values: string[] } {
    if (!rawCondition) return { field: undefined, values: [] };

    if (typeof rawCondition === 'object' && !Array.isArray(rawCondition)) {
      const fieldCandidate = rawCondition.field ?? rawCondition.key ?? rawCondition.column;
      const field = fieldCandidate ? this.resolveColumnName(allKeys, String(fieldCandidate)) : undefined;
      const valuesSource = rawCondition.values ?? rawCondition.value ?? [];
      const values = Array.isArray(valuesSource)
        ? valuesSource.map((v: any) => String(v ?? '').trim().toLowerCase()).filter(Boolean)
        : [String(valuesSource ?? '').trim().toLowerCase()].filter(Boolean);
      return { field, values };
    }

    const conditionText = String(rawCondition ?? '').trim();
    if (!conditionText) return { field: undefined, values: [] };

    const clauses = conditionText
      .split(/\s+(?:AND|OR)\s+/i)
      .map((s: string) => s.trim())
      .filter(Boolean);
    const values: string[] = [];
    let field: string | undefined;

    for (const clause of clauses) {
      const eqIdx = clause.indexOf('==') >= 0 ? clause.indexOf('==') : clause.indexOf('=');
      if (eqIdx < 0) {
        values.push(String(clause ?? '').trim().toLowerCase());
        continue;
      }
      const opLen = clause.indexOf('==') >= 0 ? 2 : 1;
      const leftSide = clause.slice(0, eqIdx).trim();
      const rightSide = clause.slice(eqIdx + opLen).trim();
      if (!field && leftSide) field = this.resolveColumnName(allKeys, leftSide || fallbackField);
      const cleaned = rightSide.replace(/[("')]/g, '').trim();
      values.push(...cleaned.split(',').map((s: string) => s.trim().toLowerCase()).filter(Boolean));
    }

    return { field: field || this.resolveColumnName(allKeys, fallbackField), values: [...new Set(values.filter(Boolean))] };
  }

  private isMathematicalCondition(conditionText: string, allKeys: string[]): boolean {
    const expression = String(conditionText ?? '').trim();
    if (!expression) {
      return false;
    }

    if (/[=<>]/.test(expression) || /\b(?:and|or|in|like)\b/i.test(expression)) {
      return false;
    }

    const sourceFields = this.parseMathematicalSourceFields(expression, allKeys);
    if (sourceFields.length < 2) {
      return false;
    }

    return sourceFields.every((fieldName: string) =>
      allKeys.some((key: string) => this.norm(key) === this.norm(fieldName))
    );
  }

  private parseMathematicalSourceFields(expression: string, allKeys: string[]): string[] {
    if (!expression) {
      return [];
    }

    return expression
      .split(/[+\-]/)
      .map((part: string) => part.trim())
      .filter(Boolean)
      .map((part: string) => this.resolveColumnName(allKeys, part));
  }

  private resolveColumnName(allKeys: string[], candidate: string): string {
    const norm = this.norm(candidate);
    return allKeys.find((k: string) => this.norm(k) === norm) || candidate;
  }

  private appendRowsToMatrix(rows: any[]): void {
    if (!rows?.length) return;
    this.resolveColumnKeysIfNeeded(rows[0]);

    for (const row of rows) {
      let parentId   = '';
      let parentNode: DrillDownNode | undefined;

      for (let lvl = 0; lvl < this.groupColumns.length; lvl++) {
        const groupLabel  = this.groupColumns[lvl];
        const resolvedKey = this.resolvedGroupKeys[lvl];
        const rawValue    = resolvedKey ? row[resolvedKey] : this.resolveRowValue(row, groupLabel);
        const cellValue   = String(rawValue ?? '').trim() || 'N/A';
        if (cellValue.toLowerCase() === 'n/a') continue;

        const nodeId = parentId ? `${parentId}||${cellValue}` : cellValue;
        const isLeaf = lvl === this.groupColumns.length - 1;

        let node = this.nodeMap.get(nodeId);
        if (!node) {
          node = {
            id: nodeId,
            name: cellValue,
            level: lvl,
            groupLabel,
            rowCount: 0,
            counts: this.createEmptyCounts(),
            sums: this.createEmptySums(),
            sumHasDollar: this.createEmptySumHasDollar(),
            distinctTracker: this.createDistinctTracker(),
            children: [],
            appRows: [],
            expanded: false,
            appsExpanded: false,
            isLeaf
          };
          this.nodeMap.set(nodeId, node);
          if (!parentNode) {
            this.matrixRoots.push(node);
          } else {
            parentNode.children.push(node);
          }
        }

        node.rowCount += 1;

        // Collect the raw data row on the leaf node for the app list
        if (isLeaf) {
          node.appRows.push(row);
        }

        parentNode = node;
        parentId   = nodeId;
      }

      // Accumulate only at the deepest node for this row.
      if (parentNode) {
        this.accumulateColumns(parentNode, row);
      }
    }

    this.rollupToParents(this.matrixRoots);

    this.syncExpandCollapseState();

    this.refreshVisibleRows();
  }

  private rollupToParents(nodes: DrillDownNode[]): void {
    for (const node of nodes) {
      if (!node.children.length) {
        continue;
      }

      this.rollupToParents(node.children);

      for (const col of this.countColumns) {
        node.counts[col] = 0;
        const isMathDistinctCount = this.metricRules.some(
          (r: DrillDownMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
        );
        if (isMathDistinctCount) {
          node.distinctTracker[col] = new Set<string>();
        }
      }
      for (const col of this.sumColumns) {
        node.sums[col] = 0;
        node.sumHasDollar[col] = false;
        const isMathDistinctSum = this.metricRules.some(
          (r: DrillDownMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
        );
        if (isMathDistinctSum) {
          node.distinctTracker[col] = new Set<string>();
        }
      }

      for (const child of node.children) {
        for (const col of this.countColumns) {
          const mathDistinctCountRule = this.metricRules.find(
            (r: DrillDownMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
          );
          if (mathDistinctCountRule) {
            const parentSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const childSet: Set<string> | undefined = child.distinctTracker[col];
            if (childSet) { childSet.forEach((v: string) => parentSet.add(v)); }
            node.counts[col] = parentSet.size;
          } else {
            node.counts[col] += child.counts[col] ?? 0;
          }
        }
        for (const col of this.sumColumns) {
          const mathDistinctSumRule = this.metricRules.find(
            (r: DrillDownMetricRule) => r.key === col && r.operation === 'mathematical' && r.distinctKey
          );
          if (mathDistinctSumRule) {
            const parentSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const childSet: Set<string> | undefined = child.distinctTracker[col];
            if (childSet) { childSet.forEach((v: string) => parentSet.add(v)); }
            node.sums[col] = parentSet.size;
          } else {
            node.sums[col] += child.sums[col] ?? 0;
            if (child.sumHasDollar[col]) { node.sumHasDollar[col] = true; }
          }
        }
      }
    }
  }

  private accumulateColumns(node: DrillDownNode, row: any): void {
    for (const rule of this.metricRules.filter(r => r.aggregate === 'count')) {
      const col = rule.key;
      if (!this.passesCondition(rule, row)) continue;

      if (rule.operation === 'mathematical' && Array.isArray(rule.sourceFields) && rule.sourceFields.length > 0) {
        // distinctKey: track distinct IDs where any source field > 0, then count the set
        if (rule.distinctKey) {
          const hasAnyPositive = rule.sourceFields.some((fieldName: string) => {
            const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
            if (!rawStr) return false;
            const parsed = this.parseNumeric(rawStr);
            return Number.isFinite(parsed) && parsed > 0;
          });
          if (hasAnyPositive) {
            const distinctSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const distinctValue = String(this.resolveRowValue(row, rule.distinctKey) ?? '').trim();
            if (distinctValue) { distinctSet.add(distinctValue); }
          }
          node.counts[col] = (node.distinctTracker[col] ?? new Set()).size;
          continue;
        }

        let rowTotal = 0;
        for (const fieldName of rule.sourceFields) {
          const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
          if (!rawStr) continue;
          const parsed = this.parseNumeric(rawStr);
          if (Number.isFinite(parsed)) {
            rowTotal += parsed;
          }
        }
        node.counts[col] = (node.counts[col] || 0) + rowTotal;
        continue;
      }

      if (rule.distinct) {
        const distinctKey = this.resolveDistinctValueKey(rule, row);
        const rawVal = this.resolveRowValue(row, distinctKey);
        const val = String(rawVal ?? '').trim();
        if (!val) continue;
        const s = node.distinctTracker[col] || new Set<string>();
        if (!s.has(this.norm(val))) {
          s.add(this.norm(val));
          node.distinctTracker[col] = s;
          node.counts[col] = (node.counts[col] || 0) + 1;
        }
        continue;
      }

      const rawValue = this.resolveRowValue(row, col);
      const rawString = String(rawValue ?? '').trim();
      if (!rawString) continue;
      const numeric = this.parseNumeric(rawString);
      node.counts[col] = (node.counts[col] || 0) + (Number.isFinite(numeric) ? numeric : 1);
    }

    for (const rule of this.metricRules.filter(r => r.aggregate === 'sum')) {
      const col = rule.key;
      if (rule.operation === 'mathematical' && Array.isArray(rule.sourceFields) && rule.sourceFields.length > 0) {
        if (rule.distinctKey) {
          const hasAnyPositive = rule.sourceFields.some((fieldName: string) => {
            const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
            if (!rawStr) return false;
            const parsed = this.parseNumeric(rawStr);
            return Number.isFinite(parsed) && parsed > 0;
          });
          if (hasAnyPositive) {
            const distinctSet: Set<string> = node.distinctTracker[col] ?? (node.distinctTracker[col] = new Set<string>());
            const distinctValue = String(this.resolveRowValue(row, rule.distinctKey) ?? '').trim();
            if (distinctValue) { distinctSet.add(distinctValue); }
          }
          node.sums[col] = (node.distinctTracker[col] ?? new Set()).size;
          continue;
        }

        let rowTotal = 0;
        let hasDollar = false;
        for (const fieldName of rule.sourceFields) {
          const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
          if (!rawStr) continue;
          if (rawStr.startsWith('$')) hasDollar = true;
          const parsed = this.parseNumeric(rawStr);
          if (Number.isFinite(parsed)) {
            rowTotal += parsed;
          }
        }
        if (hasDollar) node.sumHasDollar[col] = true;
        node.sums[col] = (node.sums[col] || 0) + rowTotal;
        continue;
      }

      const rawStr = String(this.resolveRowValue(row, col) ?? '').trim();
      if (rawStr) {
        if (rawStr.startsWith('$')) node.sumHasDollar[col] = true;
        const parsed = this.parseNumeric(rawStr);
        if (Number.isFinite(parsed)) node.sums[col] = (node.sums[col] || 0) + parsed;
      }
    }
  }

  private passesCondition(rule: DrillDownMetricRule, row: any): boolean {
    if (!rule.conditionValues?.length) return true;
    const candidates = [rule.conditionField, rule.sourceField, rule.distinctKey, rule.key].filter(Boolean) as string[];
    const field = candidates.find((c: string) => String(this.resolveRowValue(row, c) ?? '').trim() !== '') || rule.conditionField || rule.key;
    const val = String(this.resolveRowValue(row, field!) ?? '').trim().toLowerCase();
    if (!val) return false;
    return rule.conditionValues.map((v: string) => v.toLowerCase()).includes(val);
  }

  private resolveDistinctValueKey(rule: DrillDownMetricRule, row: any): string {
    // Use only rule-provided metadata fields — no hardcoded column names.
    for (const c of [rule.sourceField, rule.distinctKey, rule.key].filter(Boolean) as string[]) {
      if (String(this.resolveRowValue(row, c) ?? '').trim() !== '') return c;
    }
    return rule.sourceField || rule.distinctKey || rule.key || '';
  }

  private parseNumeric(value: string): number {
    const normalized = String(value ?? '').trim().replace(/^\((.*)\)$/, '-$1').replace(/\$/g, '').replace(/,/g, '');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  // ─── AG Grid column config ──────────────────────────────────────────────────

  private hydrateColumnConfig(): void {
    this.groupByColumns = this.resolveGroupByColumns();
    this.groupColumns   = [...this.groupByColumns];
    this.maxGroupLevel  = this.groupColumns.length;
    this.countColumns   = [];
    this.sumColumns     = [];

    for (const col of (this.columns || [])) {
      if (col.type === 'count') {
        this.countColumns.push(...col.columns.split(',').map((s: string) => s.trim()).filter(Boolean));
      } else if (col.type === 'sum') {
        this.sumColumns.push(...col.columns.split(',').map((s: string) => s.trim()).filter(Boolean));
      }
    }

    this.groupColumns   = [...new Set(this.groupColumns)];
    this.countColumns   = [...new Set(this.countColumns)];
    this.sumColumns     = [...new Set(this.sumColumns)];
    this.buildGridColumns();
  }

  private buildGridColumns(): void {
    const groupHeader = this.groupByColumns.length > 0 ? this.groupByColumns.join(', ') : 'Group';

    const groupColDef: ColDef = {
      headerName: groupHeader,
      field: 'group',
      pinned: 'left',
      sortable: true,
      resizable: true,
      cellStyle: { textAlign: 'left' },
      cellRenderer: (params: any) => {
        const row: DrillDownGridRow = params.data;
        if (!row) return '';

        // App-list row
        if (row.isAppRow) {
          const appLabel = row.appEntry?.name || 'N/A';
          const pad = (this.maxGroupLevel) * 20;
          return `<span style="display:inline-block;padding-left:${pad}px;color:#555;font-size:0.78rem">${appLabel}</span>`;
        }

        const node = row.node;
        if (!node) return '';

        const hasChildren = (node.children?.length ?? 0) > 0;
        const hasApps     = node.isLeaf && (node.appRows?.length ?? 0) > 0;
        const isExpanded  = hasChildren ? node.expanded : node.appsExpanded;
        const symbol      = (hasChildren || hasApps) ? (isExpanded ? '▼' : '▶') : '–';
        const pad         = node.level * 20;
        const weight      = (hasChildren || hasApps) ? '600' : '400';
        const cursor      = (hasChildren || hasApps) ? 'pointer' : 'default';

        return `<span style="display:inline-block;padding-left:${pad}px;font-weight:${weight};cursor:${cursor}">${symbol}&nbsp;${params.value ?? ''}</span>`;
      }
    };

    const countColDefs: ColDef[] = this.countColumns.map((col: string) => {
      const rule = this.metricRules.find((r: DrillDownMetricRule) => r.key === col);
      const pctFormula = rule?.showPercentage;
      return {
        headerName: col,
        field: `count__${this.norm(col)}`,
        sortable: true,
        resizable: true,
        headerClass: 'matrix-center-header',
        valueGetter: (p: any) => p.data?.isAppRow ? (p.data?.appEntry?.counts?.[col] ?? 0) : (p.data?.node?.counts?.[col] ?? 0),
        valueFormatter: pctFormula
          ? (p: any) => {
              const base = p.value ?? 0;
              const nodeData = p.data?.isAppRow ? p.data?.appEntry : p.data?.node;
              const pct = this.evalPercentageFormula(pctFormula, nodeData);
              return pct !== null ? `${base} (${Math.round(pct)}%)` : String(base);
            }
          : undefined,
        cellStyle: { textAlign: 'center' }
      };
    });

    const sumColDefs: ColDef[] = this.sumColumns.map((col: string) => {
      const rule = this.metricRules.find((r: DrillDownMetricRule) => r.key === col);
      const pctFormula = rule?.showPercentage;
      return {
        headerName: col,
        field: `sum__${this.norm(col)}`,
        sortable: true,
        resizable: true,
        headerClass: 'matrix-center-header',
        valueGetter: (p: any) => p.data?.isAppRow ? (p.data?.appEntry?.sums?.[col] ?? 0) : (p.data?.node?.sums?.[col] ?? 0),
        valueFormatter: (p: any) => {
          if (p.value === '' || p.value === null || typeof p.value === 'undefined') return '';
          const formatted = this.formatCompactCurrency(Number(p.value ?? 0));
          if (!pctFormula) return formatted;
          const nodeData = p.data?.isAppRow ? p.data?.appEntry : p.data?.node;
          const pct = this.evalPercentageFormula(pctFormula, nodeData);
          return pct !== null ? `${formatted} (${Math.round(pct)}%)` : formatted;
        },
        cellStyle: { textAlign: 'center' }
      };
    });

    this.matrixColumnDefs = [groupColDef, ...countColDefs, ...sumColDefs];
  }

  // ─── Cell click ─────────────────────────────────────────────────────────────

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  /** Sorting changes the visual order of nodes at every level, so any previously expanded
   *  rows are collapsed back to a clean, fully-closed state (per level ordering is otherwise ambiguous). */
  onSortChanged(): void {
    this.setExpandState(this.matrixRoots, false);
    this.syncExpandCollapseState();
    this.refreshVisibleRows();
  }

  private canUseGridApi(): boolean {
    return !!this.gridApi && !this.gridApi.isDestroyed();
  }

  private setGridRowDataSafely(rows: DrillDownGridRow[]): void {
    if (!this.canUseGridApi()) {
      return;
    }
    this.gridApi!.setGridOption('rowData', rows);
  }

  onCellClicked(event: any): void {
    if (event.colDef?.field !== 'group') return;
    const row = event.data as DrillDownGridRow;
    if (row?.isAppRow) return;

    const node = row?.node;
    if (!node) return;

    if (!node.isLeaf && node.children?.length) {
      node.expanded = !node.expanded;
      this.syncExpandCollapseState();
      this.refreshVisibleRows();
      return;
    }

    if (node.isLeaf && node.appRows?.length) {
      node.appsExpanded = !node.appsExpanded;
      this.syncExpandCollapseState();
      this.refreshVisibleRows();
    }
  }

  // ─── Expand / collapse all ──────────────────────────────────────────────────

  toggleExpandCollapseAll(): void {
    const expand = !this.allExpanded;
    this.setExpandState(this.matrixRoots, expand);
    this.syncExpandCollapseState();
    this.refreshVisibleRows();
  }

  private setExpandState(nodes: DrillDownNode[], expanded: boolean): void {
    for (const node of nodes) {
      node.expanded = expanded;
      node.appsExpanded = expanded;
      if (node.children?.length) this.setExpandState(node.children, expanded);
    }
  }

  private syncExpandCollapseState(): void {
    this.allExpanded = this.hasAnyExpandedNode(this.matrixRoots);
  }

  private hasAnyExpandedNode(nodes: DrillDownNode[]): boolean {
    for (const node of nodes || []) {
      if (node.expanded || node.appsExpanded) {
        return true;
      }
      if (node.children?.length && this.hasAnyExpandedNode(node.children)) {
        return true;
      }
    }
    return false;
  }

  // ─── Visible row management ─────────────────────────────────────────────────

  private refreshVisibleRows(): void {
    const prevScrollTop = this.getGridBodyScrollTop();
    const rows: DrillDownGridRow[] = [];

    const walk = (nodes: DrillDownNode[]) => {
      for (const node of nodes) {
        rows.push({ node, group: String(node?.name ?? '') });

        if (!node.isLeaf && node.expanded && node.children?.length) {
          walk(node.children);
        } else if (node.isLeaf && node.appsExpanded && node.appRows?.length) {
          for (const appEntry of this.buildLeafAppEntries(node)) {
            rows.push({ node, group: appEntry.name, isAppRow: true, appEntry });
          }
        }
      }
    };

    walk(this.matrixRoots);
    this.matrixRowData = rows;
    // Row count reflects the number of apps at the deepest group level, not intermediate group nodes.
    this.matrixGeneratedRowCount = this.countAllMatrixRows(this.matrixRoots);
    this.setGridRowDataSafely(this.matrixRowData);

    // The group column's cell value (node name) never changes on toggle, so AG Grid's
    // default value-diff skips re-invoking the cellRenderer — force it so the ▶/▼ icon
    // always reflects the current expanded/appsExpanded state, at every level.
    if (this.canUseGridApi()) {
      this.gridApi!.refreshCells({ force: true, columns: ['group'] });
    }

    // Re-size columns: all on first data load so metric columns fit their content;
    // group column only on subsequent expand/collapse (metric values don't change).
    setTimeout(() => {
      if (this.canUseGridApi()) {
        if (!this.metricColumnsAutoSized) {
          this.metricColumnsAutoSized = true;
          this.gridApi!.autoSizeAllColumns();
        } else {
          this.gridApi!.autoSizeColumns(['group']);
        }
        setTimeout(() => this.fitColumnsToAvailableWidth(), 0);
      }
    }, 50);

    this.restoreGridBodyScrollTop(prevScrollTop);
  }

  private buildLeafAppEntries(node: DrillDownNode): DrillDownAppEntry[] {
    const entries = new Map<string, DrillDownAppEntry>();

    for (const appRow of node.appRows || []) {
      const appId = this.buildAppEntryId(appRow);
      const appName = this.getAppDisplayName(appRow);
      const entryKey = appId || this.norm(appName) || `app-${entries.size}`;

      let entry = entries.get(entryKey);
      if (!entry) {
        entry = {
          id: entryKey,
          name: appName || 'N/A',
          counts: this.createEmptyCounts(),
          sums: this.createEmptySums(),
          sumHasDollar: this.createEmptySumHasDollar(),
          distinctTracker: this.createDistinctTracker()
        };
        entries.set(entryKey, entry);
      }

      this.accumulateColumnsOnEntry(entry, appRow);
    }

    return Array.from(entries.values()).sort((a: DrillDownAppEntry, b: DrillDownAppEntry) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' })
    );
  }

  private accumulateColumnsOnEntry(entry: DrillDownAppEntry, row: any): void {
    for (const rule of this.metricRules.filter(r => r.aggregate === 'count')) {
      const col = rule.key;
      if (!this.passesCondition(rule, row)) continue;

      if (rule.operation === 'mathematical' && Array.isArray(rule.sourceFields) && rule.sourceFields.length > 0) {
        if (rule.distinctKey) {
          const hasAnyPositive = rule.sourceFields.some((fieldName: string) => {
            const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
            if (!rawStr) return false;
            const parsed = this.parseNumeric(rawStr);
            return Number.isFinite(parsed) && parsed > 0;
          });
          if (hasAnyPositive) {
            const distinctSet: Set<string> = entry.distinctTracker[col] ?? (entry.distinctTracker[col] = new Set<string>());
            const distinctValue = String(this.resolveRowValue(row, rule.distinctKey) ?? '').trim();
            if (distinctValue) { distinctSet.add(distinctValue); }
          }
          entry.counts[col] = (entry.distinctTracker[col] ?? new Set()).size;
          continue;
        }

        let rowTotal = 0;
        for (const fieldName of rule.sourceFields) {
          const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
          if (!rawStr) continue;
          const parsed = this.parseNumeric(rawStr);
          if (Number.isFinite(parsed)) {
            rowTotal += parsed;
          }
        }
        entry.counts[col] = (entry.counts[col] || 0) + rowTotal;
        continue;
      }

      if (rule.distinct) {
        const distinctKey = this.resolveDistinctValueKey(rule, row);
        const rawVal = this.resolveRowValue(row, distinctKey);
        const val = String(rawVal ?? '').trim();
        if (!val) continue;

        const distinctSet = entry.distinctTracker[col] || new Set<string>();
        const normalizedValue = this.norm(val);
        if (!distinctSet.has(normalizedValue)) {
          distinctSet.add(normalizedValue);
          entry.distinctTracker[col] = distinctSet;
          entry.counts[col] = (entry.counts[col] || 0) + 1;
        }
        continue;
      }

      const rawValue = this.resolveRowValue(row, col);
      const rawString = String(rawValue ?? '').trim();
      if (!rawString) continue;

      const numeric = this.parseNumeric(rawString);
      entry.counts[col] = (entry.counts[col] || 0) + (Number.isFinite(numeric) ? numeric : 1);
    }

    for (const rule of this.metricRules.filter(r => r.aggregate === 'sum')) {
      const col = rule.key;
      if (rule.operation === 'mathematical' && Array.isArray(rule.sourceFields) && rule.sourceFields.length > 0) {
        if (rule.distinctKey) {
          const hasAnyPositive = rule.sourceFields.some((fieldName: string) => {
            const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
            if (!rawStr) return false;
            const parsed = this.parseNumeric(rawStr);
            return Number.isFinite(parsed) && parsed > 0;
          });
          if (hasAnyPositive) {
            const distinctSet: Set<string> = entry.distinctTracker[col] ?? (entry.distinctTracker[col] = new Set<string>());
            const distinctValue = String(this.resolveRowValue(row, rule.distinctKey) ?? '').trim();
            if (distinctValue) { distinctSet.add(distinctValue); }
          }
          entry.sums[col] = (entry.distinctTracker[col] ?? new Set()).size;
          continue;
        }

        let rowTotal = 0;
        let hasDollar = false;
        for (const fieldName of rule.sourceFields) {
          const rawStr = String(this.resolveRowValue(row, fieldName) ?? '').trim();
          if (!rawStr) continue;
          if (rawStr.startsWith('$')) hasDollar = true;
          const parsed = this.parseNumeric(rawStr);
          if (Number.isFinite(parsed)) {
            rowTotal += parsed;
          }
        }
        if (hasDollar) entry.sumHasDollar[col] = true;
        entry.sums[col] = (entry.sums[col] || 0) + rowTotal;
        continue;
      }

      const rawStr = String(this.resolveRowValue(row, col) ?? '').trim();
      if (!rawStr) continue;

      if (rawStr.startsWith('$')) {
        entry.sumHasDollar[col] = true;
      }

      const parsed = this.parseNumeric(rawStr);
      if (Number.isFinite(parsed)) {
        entry.sums[col] = (entry.sums[col] || 0) + parsed;
      }
    }
  }

  private buildAppEntryId(appRow: any): string {
    const appParts = this.appNameColumns
      .map((col: string) => this.resolveRowValue(appRow, col))
      .filter((value: string) => !!value);

    if (appParts.length > 0) {
      return appParts.map((value: string) => this.norm(value)).join('||');
    }

    return '';
  }

  private getAppDisplayName(appRow: any): string {
    for (const col of this.appNameColumns) {
      const value = this.resolveRowValue(appRow, col);
      if (value) {
        return value;
      }
    }

    return '';
  }

  // ─── Sort helpers ────────────────────────────────────────────────────────────

  private buildSortedVisibleRowNodes(inputRowNodes: any[]): any[] {
    const byId = new Map<string, any>();
    inputRowNodes.forEach((rowNode: any) => {
      const key = this.getRowSortKey(rowNode?.data);
      if (key) byId.set(key, rowNode);
    });

    const ordered: any[] = [];
    const walk = (nodes: DrillDownNode[]) => {
      const sortedLevel = this.getSortedNodesForLevel(nodes || []);
      for (const node of sortedLevel) {
        const rowNode = byId.get(node.id);
        if (rowNode) ordered.push(rowNode);

        if (!node.isLeaf && node.expanded && node.children?.length) {
          walk(node.children);
        } else if (node.isLeaf && node.appsExpanded && node.appRows?.length) {
          for (const appEntry of this.buildLeafAppEntries(node)) {
            const appKey = this.getRowSortKey({ node, group: appEntry.name, isAppRow: true, appEntry });
            const appRowNode = appKey ? byId.get(appKey) : undefined;
            if (appRowNode) ordered.push(appRowNode);
          }
        }
      }
    };
    walk(this.matrixRoots);
    return ordered;
  }

  /** Unique key per grid row (group node row vs. one of its app-list rows) so rows sharing the same node don't collide. */
  private getRowSortKey(data: DrillDownGridRow | undefined): string | null {
    if (!data?.node?.id) return null;
    return data.isAppRow ? `${data.node.id}::app::${data.appEntry?.id ?? data.appEntry?.name ?? ''}` : data.node.id;
  }

  private getSortedNodesForLevel(nodes: DrillDownNode[]): DrillDownNode[] {
    const sortState = this.getActiveSortState();
    if (!sortState || nodes.length < 2) return [...nodes];
    const sorted = [...nodes];
    sorted.sort((a, b) => {
      const cmp = this.compareNodesBySortState(a, b, sortState);
      if (cmp !== 0) return sortState.sort === 'asc' ? cmp : -cmp;
      return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
    });
    return sorted;
  }

  private getActiveSortState(): ColumnState | null {
    const states = this.gridApi?.getColumnState?.() || [];
    return states.find((s: ColumnState) => s.sort === 'asc' || s.sort === 'desc') || null;
  }

  private compareNodesBySortState(a: DrillDownNode, b: DrillDownNode, sortState: ColumnState): number {
    const colId = String(sortState.colId ?? '');
    if (colId === 'group') return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
    if (colId.startsWith('count__')) {
      const key = this.findMetricKeyByColId(colId, 'count');
      return (key ? Number(a.counts?.[key] ?? 0) : 0) - (key ? Number(b.counts?.[key] ?? 0) : 0);
    }
    if (colId.startsWith('sum__')) {
      const key = this.findMetricKeyByColId(colId, 'sum');
      return (key ? Number(a.sums?.[key] ?? 0) : 0) - (key ? Number(b.sums?.[key] ?? 0) : 0);
    }
    return 0;
  }

  private findMetricKeyByColId(colId: string, type: 'count' | 'sum'): string | null {
    const prefix = `${type}__`;
    const normalizedKey = colId.startsWith(prefix) ? colId.slice(prefix.length) : colId;
    const candidates = type === 'count' ? this.countColumns : this.sumColumns;
    return candidates.find((k: string) => this.norm(k) === normalizedKey) || null;
  }

  // ─── Download ────────────────────────────────────────────────────────────────

  downloadAndExportToExcel(): void {
    if (this.isDownloadDisabled) return;
    this.isExporting = true;
    this.isDownloadDisabled = true;
    try {
      const rowsToExport = [...(this.rawTableDataForDownload || [])];
      if (rowsToExport.length === 0) {
        this.toastr.error('No data available to export', '', { timeOut: 3000, positionClass: 'toast-top-center' });
        return;
      }
      this.exportDataService.exportToExcel(this.config?.reportNameFromQueryParams || 'drill-down-matrix', [rowsToExport]);
    } finally {
      this.isExporting = false;
      this.isDownloadDisabled = false;
    }
  }

  // ─── Key resolution & row value helpers ────────────────────────────────────

  private resolveColumnKeysIfNeeded(sampleRow: any): void {
    if (!sampleRow) return;
    const allKeys = Object.keys(sampleRow);
    if (this.resolvedGroupKeys.length === 0) {
      this.resolvedGroupKeys = this.groupColumns.map((g: string) => allKeys.find((k: string) => this.norm(k) === this.norm(g)));
    }
    if (this.resolvedCountKeys.length === 0) {
      this.resolvedCountKeys = this.countColumns.map((c: string) => allKeys.find((k: string) => this.norm(k) === this.norm(c)));
    }
    if (this.resolvedSumKeys.length === 0) {
      this.resolvedSumKeys = this.sumColumns.map((s: string) => allKeys.find((k: string) => this.norm(k) === this.norm(s)));
    }
  }

  private resolveRowValue(row: any, key: string): string {
    const nk = this.norm(key);
    for (const col of Object.keys(row || {})) {
      if (this.norm(col) === nk) return String(row[col] ?? '').trim();
    }
    return '';
  }

  private createEmptyCounts(): Record<string, number> {
    return Object.fromEntries(this.countColumns.map((c: string) => [c, 0]));
  }

  private createEmptySums(): Record<string, number> {
    return Object.fromEntries(this.sumColumns.map((s: string) => [s, 0]));
  }

  private createEmptySumHasDollar(): Record<string, boolean> {
    return Object.fromEntries(this.sumColumns.map((s: string) => [s, false]));
  }

  private createDistinctTracker(): Record<string, Set<string>> {
    return Object.fromEntries(this.countColumns.map((c: string) => [c, new Set<string>()]));
  }

  private getGridBodyScrollTop(): number {
    const viewport = this.matrixContainer?.nativeElement?.querySelector('.ag-body-viewport') as HTMLElement | null;
    return viewport?.scrollTop ?? 0;
  }

  /** Content-sized columns can leave a large blank gap on wide screens; stretch them to
   *  fill the grid only when doing so wouldn't force a horizontal scrollbar. When the
   *  auto-sized content is already wider than the viewport, leave widths as-is so the
   *  user still gets a horizontal scroll instead of squeezed/truncated columns. */
  private fitColumnsToAvailableWidth(): void {
    if (!this.canUseGridApi()) return;
    const viewport = this.matrixContainer?.nativeElement?.querySelector('.ag-center-cols-viewport') as HTMLElement | null;
    if (!viewport) return;
    if (viewport.scrollWidth <= viewport.clientWidth) {
      // Pin the group column's own auto-sized width so sizeColumnsToFit only stretches the metric columns.
      const groupWidth = this.gridApi!.getColumn('group')?.getActualWidth();
      this.gridApi!.sizeColumnsToFit(
        groupWidth ? { columnLimits: [{ key: 'group', minWidth: groupWidth, maxWidth: groupWidth }] } : undefined
      );
    }
  }

  private restoreGridBodyScrollTop(scrollTop: number): void {
    setTimeout(() => {
      const viewport = this.matrixContainer?.nativeElement?.querySelector('.ag-body-viewport') as HTMLElement | null;
      if (viewport) viewport.scrollTop = scrollTop;
    }, 0);
  }

  /**
   * Evaluates a showPercentage formula against a node's or app entry's aggregated values.
   * Column names in the formula are replaced with their numeric values from sums / counts.
   * Returns the computed number (0–100+ range), or null if the formula cannot be evaluated.
   */
  private evalPercentageFormula(formula: string, node: { counts: Record<string, number>; sums: Record<string, number> } | undefined): number | null {
    if (!formula || !node) return null;

    // Collect all column values; sums take precedence over counts for the same key.
    const colValues = new Map<string, number>();
    for (const [k, v] of Object.entries(node.counts || {})) {
      colValues.set(k, Number(v ?? 0));
    }
    for (const [k, v] of Object.entries(node.sums || {})) {
      colValues.set(k, Number(v ?? 0));
    }

    // Replace column names with numeric values, longest names first to avoid partial matches.
    let expr = formula;
    const sortedKeys = Array.from(colValues.keys()).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(escaped, 'gi');
      expr = expr.replace(re, String(colValues.get(key) ?? 0));
    }

    return this.safeArithmetic(expr.trim());
  }

  /**
   * Evaluates a pure arithmetic expression string containing only digits, decimal points,
   * +, -, *, /, and parentheses.  No eval() / Function() is used.
   */
  private safeArithmetic(expr: string): number | null {
    // Guard: allow only arithmetic characters to prevent injection.
    if (!/^[\d\s+\-*/().]+$/.test(expr)) return null;

    const tokens = expr.match(/[\d.]+|[+\-*/()]/g);
    if (!tokens) return null;

    let pos = 0;

    const parsePrimary = (): number => {
      if (tokens[pos] === '(') {
        pos++;
        const val = parseAddSub();
        if (tokens[pos] === ')') pos++;
        return val;
      }
      return parseFloat(tokens[pos++] ?? '0');
    };

    const parseMulDiv = (): number => {
      let val = parsePrimary();
      while (pos < tokens.length && (tokens[pos] === '*' || tokens[pos] === '/')) {
        const op = tokens[pos++];
        const rhs = parsePrimary();
        val = op === '*' ? val * rhs : (rhs !== 0 ? val / rhs : 0);
      }
      return val;
    };

    const parseAddSub = (): number => {
      let val = parseMulDiv();
      while (pos < tokens.length && (tokens[pos] === '+' || tokens[pos] === '-')) {
        const op = tokens[pos++];
        const rhs = parseMulDiv();
        val = op === '+' ? val + rhs : val - rhs;
      }
      return val;
    };

    try {
      const result = parseAddSub();
      return isFinite(result) ? result : null;
    } catch {
      return null;
    }
  }

  /** Counts app rows at the deepest (leaf) level rather than every intermediate group node. */
  private countAllMatrixRows(nodes: DrillDownNode[]): number {
    let count = 0;
    const walk = (input: DrillDownNode[]) => {
      for (const node of input || []) {
        if (node.isLeaf) {
          count += node.appRows?.length ? this.buildLeafAppEntries(node).length : 0;
        } else if (node.children?.length) {
          walk(node.children);
        }
      }
    };
    walk(nodes || []);
    return count;
  }

  private formatCompactCurrency(value: number): string {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1_000_000_000) return `${sign}$${(absValue / 1_000_000_000).toFixed(2)}B`;
    if (absValue >= 1_000_000)     return `${sign}$${(absValue / 1_000_000).toFixed(2)}M`;
    if (absValue >= 1_000)         return `${sign}$${(absValue / 1_000).toFixed(2)}K`;
    return `${sign}$${absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private updateGmtTimestamp(): void {
    const now = new Date();
    this.currentDate = now;
    this.currentTime = `${this.datePipe.transform(now, 'hh:mm a', 'GMT') || ''} GMT`;
  }

  private resolveGroupByColumns(): string[] {
    const configGroupByColumns = this.elementData?.config?.groupByColumns ?? this.config?.groupByColumns;
    if (Array.isArray(configGroupByColumns)) return configGroupByColumns.map((i: any) => String(i ?? '').trim()).filter(Boolean);
    if (typeof configGroupByColumns === 'string') return configGroupByColumns.split(',').map((i: string) => i.trim()).filter(Boolean);
    const legacyGroupColumns = this.elementData?.config?.groupColumns ?? this.config?.groupColumns;
    if (Array.isArray(legacyGroupColumns)) return legacyGroupColumns.map((i: any) => String(i ?? '').trim()).filter(Boolean);
    if (typeof legacyGroupColumns === 'string') return legacyGroupColumns.split(':').map((i: string) => i.trim()).filter(Boolean);
    return [];
  }

  private resolveAppNameColumns(allKeys: string[]): string[] {
    const appNameCols = this.elementData?.config?.appNameColumns ?? this.config?.appNameColumns;
    if (Array.isArray(appNameCols)) return appNameCols.map((c: any) => String(c ?? '').trim()).filter(Boolean);
    if (typeof appNameCols === 'string') return appNameCols.split(',').map((c: string) => c.trim()).filter(Boolean);

    const preferredAppColumns = (allKeys || [])
      .map((key: string) => ({ key, score: this.getAppColumnScore(key) }))
      .filter((candidate: { key: string; score: number }) => candidate.score > 0)
      .sort((left: { key: string; score: number }, right: { key: string; score: number }) => right.score - left.score)
      .map((candidate: { key: string; score: number }) => candidate.key);

    if (preferredAppColumns.length > 0) {
      return [preferredAppColumns[0]];
    }

    const excludedKeys = new Set([
      ...this.groupColumns,
      ...this.countColumns,
      ...this.sumColumns
    ].map((key: string) => this.norm(key)));

    const candidateColumns = (allKeys || []).filter((key: string) => !excludedKeys.has(this.norm(key)));
    if (candidateColumns.length === 0) {
      return [];
    }

    return [candidateColumns[0]];
  }

  private getAppColumnScore(key: string): number {
    const normalizedKey = this.norm(key);

    if (normalizedKey === 'apps') {
      return 110;
    }

    if (normalizedKey === 'appname' || normalizedKey === 'applicationname' || normalizedKey === 'app_name' || normalizedKey === 'longappname') {
      return 100;
    }

    if (normalizedKey.includes('applicationname') || normalizedKey.includes('appname')) {
      return 90;
    }

    if (normalizedKey === 'application' || normalizedKey === 'app') {
      return 80;
    }

    if (normalizedKey.includes('application') || normalizedKey.includes('app')) {
      return 70;
    }

    return 0;
  }

  // ─── Clear helpers ──────────────────────────────────────────────────────────

  private clearMatrixData(): void {
    this.matrixRoots = [];
    this.matrixRowData = [];
    this.matrixGeneratedRowCount = 0;
    this.nodeMap.clear();
    this.resolvedGroupKeys = [];
    this.resolvedCountKeys = [];
    this.resolvedSumKeys   = [];
    this.allExpanded = false;
    this.setGridRowDataSafely([]);
  }

  private handleErrors(err: any): void {
    console.error('Drill-down matrix error:', err);
    this.toastr.error('An error occurred while fetching drill-down matrix data. Please try again later.', 'Error', {
      timeOut: 5000,
      positionClass: 'toast-top-center',
    });
  }
}
